import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import './LoginPage.css';

const LoginPage = () => {
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  const navigate = useNavigate();

  // Check if account is locked from localStorage on component mount
  useEffect(() => {
    const lockExpiration = localStorage.getItem('loginLockExpiration');
    if (lockExpiration && new Date().getTime() < parseInt(lockExpiration)) {
      setIsLocked(true);
      const timeLeft = Math.ceil((parseInt(lockExpiration) - new Date().getTime()) / 1000);
      setLockTimer(timeLeft);
    }
  }, []);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (isLocked && lockTimer > 0) {
      interval = setInterval(() => {
        setLockTimer((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            localStorage.removeItem('loginLockExpiration');
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isLocked, lockTimer]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Check if account is locked
    if (isLocked) {
      setError(`Account is locked. Please try again in ${lockTimer} seconds`);
      return;
    }

    // Validate nickname is not empty
    if (!nickname.trim()) {
      setError('Please enter your nickname');
      return;
    }

    // Construct email internally
    const email = nickname.includes('@') ? nickname.toLowerCase() : `${nickname.toLowerCase()}@hohopost.com`;

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Reset login attempts on successful login
      setLoginAttempts(0);
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('loginLockExpiration');
      // Redirect to the user's tree page
      navigate(`/tree/${user.uid}`);
    } catch (err) {
      // Increment login attempts
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      // Lock account after 3 failed attempts
      if (newAttempts >= 3) {
        const lockDuration = 300000; // 5 minutes in milliseconds
        const lockExpiration = new Date().getTime() + lockDuration;
        setIsLocked(true);
        setLockTimer(300); // 300 seconds
        localStorage.setItem('loginLockExpiration', lockExpiration.toString());
        setError('Too many failed attempts. Account locked for 5 minutes.');
      } else {
        setError(`Invalid credentials. ${3 - newAttempts} attempts remaining.`);
      }
    }
  };

  return (
    <div className="christmas-login-page">
      <div className="lights-container">
        <video
          className="christmas-lights-video"
          autoPlay
          loop
          muted
          playsInline
          src={`${process.env.PUBLIC_URL}/christmas_lights.mp4`}
        >
          <source src={`${process.env.PUBLIC_URL}/christmas_lights.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      <header className="christmas-header">
        <h1 className="page-title">Welcome Back</h1>
        <p>Your tree misses you.</p>
      </header>

      <div className="login-card">
        <h2 className="login-title">Login</h2>
        <form onSubmit={handleLogin}>
          <input
            type="text"
            placeholder="Nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className="login-input"
            required
            disabled={isLocked}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            required
            disabled={isLocked}
          />
          <button type="submit" className="login-button" disabled={isLocked}>
            🎄 Login
          </button>
        </form>
        {error && <p className="error-message">{error}</p>}
        {isLocked && (
          <p className="lock-timer">
            Account locked. Try again in {lockTimer} seconds
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;